import type { FeedbackAnswerQuestion, QuestionExportAnswer } from '@repo/types/questions/Question';
import type { ExportAnswerFill, FeedbackAnswerQuestionFill, QuestionFillData } from '@repo/types/questions/QuestionFill';
import type { ExportAnswerLong, FeedbackAnswerQuestionLong, QuestionLongData } from '@repo/types/questions/QuestionLong';
import type { ExportAnswerMatching, FeedbackAnswerQuestionMatching, QuestionMatchingData } from '@repo/types/questions/QuestionMatching';
import type { ExportAnswerMultiple, FeedbackAnswerQuestionMultiple, QuestionMultipleData } from '@repo/types/questions/QuestionMultiple';
import type { ExportAnswerSingle, FeedbackAnswerQuestionSingle, QuestionSingleData } from '@repo/types/questions/QuestionSingle';
import type { ExportAnswerTranslate, FeedbackAnswerQuestionTranslate, QuestionTranslateData } from '@repo/types/questions/QuestionTranslate';
import type { ExportAnswerVoice, FeedbackAnswerQuestionVoice, QuestionVoiceData } from '@repo/types/questions/QuestionVoice';
import type { QuestionBase } from '@repo/types/quiz';

import { QuestionType } from '@repo/types/enums/questionType';

import {
  QuestionMarkedFill,
  QuestionMarkedLong,
  QuestionMarkedMatching,
  QuestionMarkedMultiple,
  QuestionMarkedSingle,
  QuestionMarkedTranslate,
  QuestionMarkedVoice,
} from '@/components/features/quiz/question/question-marked';

export interface QuestionMarkedTypeProps {
  question: QuestionBase;
  marked?: FeedbackAnswerQuestion;
  answer?: QuestionExportAnswer;
  showQuestion?: boolean;
  showMarked?: boolean;
  onEditMark?: (data: FeedbackAnswerQuestion) => void;
}

export function QuestionMarkedType(questionMarkedTypeProps: QuestionMarkedTypeProps) {
  const { question, marked, answer, showMarked = true, showQuestion = true, onEditMark } = questionMarkedTypeProps;
  switch (question.type) {
    case QuestionType.SINGLE:
      return (
        <QuestionMarkedSingle
          marked={marked as FeedbackAnswerQuestionSingle | null}
          answer={answer as ExportAnswerSingle}
          question={question as QuestionSingleData}
          showMarked={showMarked}
          showQuestion={showQuestion}
          onEditMark={onEditMark}
        />
      );
    case QuestionType.MULTIPLE:
      return (
        <QuestionMarkedMultiple
          marked={marked as FeedbackAnswerQuestionMultiple | null}
          answer={answer as ExportAnswerMultiple | undefined}
          question={question as QuestionMultipleData}
          showMarked={showMarked}
          showQuestion={showQuestion}
          onEditMark={onEditMark}
        />
      );
    case QuestionType.FILL:
      return (
        <QuestionMarkedFill
          marked={marked as FeedbackAnswerQuestionFill | null}
          answer={answer as ExportAnswerFill | undefined}
          question={question as QuestionFillData}
          showMarked={showMarked}
          showQuestion={showQuestion}
          onEditMark={onEditMark}
        />
      );
    case QuestionType.MATCHING:
      return (
        <QuestionMarkedMatching
          marked={marked as FeedbackAnswerQuestionMatching | null}
          answer={answer as ExportAnswerMatching | undefined}
          question={question as QuestionMatchingData}
          showMarked={showMarked}
          showQuestion={showQuestion}
          onEditMark={onEditMark}
        />
      );
    case QuestionType.TRANSLATE:
      return (
        <QuestionMarkedTranslate
          marked={marked as FeedbackAnswerQuestionTranslate | null}
          answer={answer as ExportAnswerTranslate | undefined}
          question={question as QuestionTranslateData}
          showMarked={showMarked}
          showQuestion={showQuestion}
          onEditMark={onEditMark}
        />
      );

    case QuestionType.VOICE:
      return (
        <QuestionMarkedVoice
          marked={marked as FeedbackAnswerQuestionVoice | null}
          answer={answer as ExportAnswerVoice | undefined}
          question={question as QuestionVoiceData}
          showMarked={showMarked}
          showQuestion={showQuestion}
          onEditMark={onEditMark}
        />
      );
    case QuestionType.LONG:
      return (
        <QuestionMarkedLong
          marked={marked as FeedbackAnswerQuestionLong | null}
          answer={answer as ExportAnswerLong | undefined}
          question={question as QuestionLongData}
          showMarked={showMarked}
          showQuestion={showQuestion}
          onEditMark={onEditMark}
        />
      );

    default:
      return <p className="text-destructive w-full py-14 text-center text-sm">Ten typ pytania nie jest obsługiwany</p>;
  }
}
